import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_COLORS = {
  suggestion: 'bg-blue-100 text-blue-700',
  problem: 'bg-red-100 text-red-700',
  feature: 'bg-purple-100 text-purple-700',
  praise: 'bg-green-100 text-green-700',
};
const STATUS_NEXT = { new: 'reviewed', reviewed: 'resolved', resolved: 'new' };
const STATUS_COLORS = {
  new: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
};

export default function AdminFeedback({ getAuthHeaders }) {
  const [data, setData] = useState({ feedback: [], stats: { total: 0, new: 0, by_category: {} } });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (catFilter) params.set('category', catFilter);
      const res = await fetch(`${API}/api/admin/feedback?${params.toString()}`, { headers: getAuthHeaders() });
      const d = await res.json();
      if (res.ok) setData(d);
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [getAuthHeaders, statusFilter, catFilter]);

  useEffect(() => { load(); }, [load]);

  const cycleStatus = async (item) => {
    const next = STATUS_NEXT[item.status] || 'reviewed';
    try {
      const res = await fetch(`${API}/api/admin/feedback/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ status: next }),
      });
      if (res.ok) load();
    } catch (e) { /* noop */ }
  };

  const stats = data.stats || {};

  return (
    <div className="space-y-6" data-testid="admin-feedback">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-bold text-slate-900">{stats.total || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">New</p><p className="text-2xl font-bold text-amber-600">{stats.new || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Problems</p><p className="text-2xl font-bold text-red-600">{(stats.by_category || {}).problem || 0}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Suggestions</p><p className="text-2xl font-bold text-blue-600">{(stats.by_category || {}).suggestion || 0}</p></CardContent></Card>
      </div>

      <div className="flex flex-wrap gap-2">
        {['', 'new', 'reviewed', 'resolved'].map((s) => (
          <button key={s || 'all'} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${statusFilter === s ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid={`feedback-filter-status-${s || 'all'}`}>
            {s ? s[0].toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
        <span className="w-px bg-slate-200 mx-1" />
        {['', 'suggestion', 'problem', 'feature', 'praise'].map((c) => (
          <button key={c || 'allcat'} onClick={() => setCatFilter(c)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${catFilter === c ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200'}`}
            data-testid={`feedback-filter-cat-${c || 'all'}`}>
            {c ? c[0].toUpperCase() + c.slice(1) : 'All Types'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : data.feedback.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="admin-feedback-empty">
          <Inbox className="h-10 w-10 mx-auto mb-2" />
          <p>No feedback yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.feedback.map((f) => (
            <Card key={f.id} data-testid={`admin-feedback-item-${f.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className={`text-[10px] ${CAT_COLORS[f.category] || ''}`}>{f.category}</Badge>
                  <Badge className={`text-[10px] ${STATUS_COLORS[f.status] || ''}`}>{f.status}</Badge>
                  <span className="text-xs font-medium text-slate-700">{f.business_name}</span>
                  <span className="text-xs text-slate-400">{f.client_email}</span>
                  <span className="ml-auto text-[10px] text-slate-400">{(f.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
                <p className="text-sm text-slate-700">{f.message}</p>
                {f.page && <p className="text-[10px] text-slate-400 mt-1">Page: {f.page}</p>}
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => cycleStatus(f)} data-testid={`feedback-cycle-${f.id}`}>
                    Mark as {STATUS_NEXT[f.status]}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
